import React, { useMemo } from "react";
import { DataTable } from "@/components/ui/DataTable/index";
import { type DataTableColumn } from "@/components/ui/DataTable/index";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import moment from "moment-timezone";
import { useSearchParams } from "react-router-dom";
import {
  useListDestinationLocations,
  DestinationLocationImproved,
} from "@/lib/api/destinationHoursImproved";
import { CommonDestinationTimezone } from "@/lib/utils";

interface LocationsListViewProps {
  onLocationSelect: (locationId: string) => void;
}

export function LocationsListView({ onLocationSelect }: LocationsListViewProps) {
  const locationsQuery = useListDestinationLocations();
  const [, setSearchParams] = useSearchParams();

  const handleLatestRunClick = (locationId: string, runId: string) => {
    setSearchParams((prev: URLSearchParams) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("tab", "run-hours");
      newParams.set("location_id", locationId);
      newParams.set("run_id", runId);
      return newParams;
    });
  };

  const columns = useMemo((): DataTableColumn<DestinationLocationImproved>[] => [
    {
      id: "name",
      accessorKey: "name",
      header: "Location Name",
      accessorFn: (row: any) => row.alias || row.name,
      cell: (info) => info.getValue() as string,
      minSize: 200,
      maxSize: 300,
      enableSorting: true,
    },
    {
      id: "is_enabled",
      accessorKey: "is_enabled",
      header: "Status",
      cell: (info) => (
        <Badge variant={info.getValue() ? "default" : "secondary"}>
          {info.getValue() ? "Enabled" : "Disabled"}
        </Badge>
      ),
      minSize: 100,
      maxSize: 120,
      enableSorting: true,
    },
    {
      id: "is_visible",
      accessorKey: "is_visible",
      header: "Visibility",
      cell: (info) => (
        <Badge variant={info.getValue() ? "default" : "outline"}>
          {info.getValue() ? "Visible" : "Hidden"}
        </Badge>
      ),
      minSize: 100,
      maxSize: 120,
      enableSorting: true,
    },
    {
      id: "last_fetched",
      accessorKey: "last_fetched",
      header: "Last Fetched",
      cell: (info) => {
        const date = info.getValue() as Date | null;
        return date ? moment.tz(date, CommonDestinationTimezone).format("MMM DD, YYYY hh:mm a") : "Never";
      },
      minSize: 150,
      maxSize: 180,
      enableSorting: true,
    },
    {
      id: "serviceCategories",
      accessorKey: "serviceCategories",
      header: "Service Categories",
      cell: (info) => {
        const categories = info.getValue() as DestinationLocationImproved["serviceCategories"];
        const activeCount = categories?.filter(cat => cat.is_active).length || 0;
        const totalCount = categories?.length || 0;
        return `${activeCount}/${totalCount} active`;
      },
      minSize: 120,
      maxSize: 150,
      enableSorting: false,
      enableFiltering: false,
    },
    {
      id: "latest_run",
      accessorKey: "latest_run_id",
      header: "Latest Run",
      cell: (info) => {
        const latestRunId = info.getValue() as string | undefined;
        const row = info.row.original;

        if (!latestRunId) {
          return <span className="text-gray-400">No runs</span>;
        }

        return (
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleLatestRunClick(row.id, latestRunId);
            }}
          >
            View Latest
          </Button>
        );
      },
      minSize: 100,
      maxSize: 120,
      enableSorting: false,
      enableFiltering: false,
      disableRowClick: true
    },

  ], [onLocationSelect, handleLatestRunClick]);

  const isLoading = locationsQuery.isLoading;
  const locations = locationsQuery.data?.data || [];

  return (
    <div className="space-y-4">
      <DataTable
        className="shadow-lg"
        stickyHeaders
        tableClassName="max-h-[calc(100vh-300px)] overflow-y-scroll"
        data={locations}
        columns={columns}
        enableSorting
        enableMultiSort
        enableFiltering
        enableColumnVisibility
        enableColumnPinning
        enableExport
        filterStyle="select"
        filterMode="internal"
        rowClickable
        onRowClick={(row) => onLocationSelect(row.id)}
        emptyMessage={
          isLoading
            ? "Loading locations..."
            : "No destination locations found"
        }
      />
    </div>
  );
}
